import qs from "qs";
import ApiHelper from "./CoreHelpers/ApiHelper";
import FilterOptionsHelper from "./CoreHelpers/FilterOptionsHelper";
import { hasRangeFilterBound, normalizeRangeFilterDate } from "./CoreHelpers/rangeFilterHelpers";

export const EXACT_SEARCH_FIELDS = ["ticker", "market", "ticker_as_link"];

const EXACT_SEARCH_FIELD_SET = new Set(EXACT_SEARCH_FIELDS);

function isPreviousSearchQueryKey(key) {
  return (
    /__(icontains|exact)$/.test(key) || EXACT_SEARCH_FIELD_SET.has(key)
  );
}

export class UniversalTableQuery {
  queryObject = {};

  addOrdering(sortBy): UniversalTableQuery {
    if (!sortBy.field) {
      delete this.queryObject.ordering;
    } else {
      this.queryObject.ordering =
        sortBy.direction === "asc" ? sortBy.field : `-${sortBy.field}`;
    }

    return this;
  }

  addLimit(pagination): UniversalTableQuery {
    this.queryObject.limit = pagination.rowsPerPage;

    return this;
  }

  addOffset(pagination): UniversalTableQuery {
    if (pagination.current > 1) {
      this.queryObject.offset =
        pagination.rowsPerPage * (pagination.current - 1);
    }

    return this;
  }

  addSearch(search): UniversalTableQuery {
    Object.keys(this.queryObject).forEach(key => {
      if (isPreviousSearchQueryKey(key)) {
        delete this.queryObject[key];
      }
    });

    for (let field in search) {
      if (search.hasOwnProperty(field)) {
        if (EXACT_SEARCH_FIELD_SET.has(field)) {
          this.queryObject[field] = search[field];
        } else {
          this.queryObject[`${field}__icontains`] = search[field];
        }
      }
    }

    return this;
  }

  addFilters(filters, columns): UniversalTableQuery {
    for (let field in filters) {
      if (filters.hasOwnProperty(field)) {
        const correspondingColumnData = columns.find(column =>
          ApiHelper.compareColumnAttributeNameForFiltering(column, field)
        );
        const filterValue = filters[field];

        if (typeof filterValue.min !== "undefined") {
          this.addRangeFilter(
            field,
            filterValue,
            ApiHelper.getIsDateRange(correspondingColumnData),
            correspondingColumnData
          );
        } else if (Array.isArray(filterValue)) {
          if (ApiHelper.getIsFilterableByRuleset(correspondingColumnData)) {
            this.addRulesetFilter(field, filterValue, correspondingColumnData);
          } else {
            this.addValueMultiSelectFilter(field, filterValue);
          }
        } else if (
          correspondingColumnData &&
          ApiHelper.getIsFilterableByRadio(correspondingColumnData) &&
          (typeof filterValue === "string" ||
            typeof filterValue === "number")
        ) {
          const optionAll =
            Array.isArray(correspondingColumnData.filter_data) &&
            correspondingColumnData.filter_data.includes("all");
          if (!(optionAll && filterValue === "all")) {
            this.queryObject[field] = filterValue;
          }
        }
      }
    }

    return this;
  }

  addRangeFilter(field, value, isDateRange, column): void {
    if (
      typeof value.min !== "undefined" &&
      hasRangeFilterBound(value.min, isDateRange, column)
    ) {
      this.queryObject[`${field}__gte`] = isDateRange
        ? normalizeRangeFilterDate(value.min)
        : value.min;
    }

    if (
      typeof value.max !== "undefined" &&
      hasRangeFilterBound(value.max, isDateRange, column)
    ) {
      this.queryObject[`${field}__lte`] = isDateRange
        ? normalizeRangeFilterDate(value.max)
        : value.max;
    }
  }

  addValueMultiSelectFilter(field, value): void {
    this.queryObject[`${field}__in`] = value.join(",");
  }

  addRulesetFilter(field, value, column): void {
    if (FilterOptionsHelper.isTrendStatusColumn(column) && value.length > 1) {
      const exactValues = value
        .map(selectedValue => {
          const rule = column.filter_data[selectedValue];

          if (!rule || typeof rule !== "object") {
            return null;
          }

          const [direction, ruleValue] = Object.entries(rule)[0] || [];

          if (direction === "eq" || direction === "exact") {
            return ruleValue;
          }

          return null;
        })
        .filter(
          ruleValue => ruleValue !== null && typeof ruleValue !== "undefined"
        );

      if (exactValues.length === value.length) {
        this.queryObject[`${field}__in`] = exactValues.join(",");
        return;
      }
    }

    const selectedRule = this.getSelectedRulesetRule(value, column);
    if (!selectedRule) {
      return;
    }

    const [direction, ruleValue] = Object.entries(selectedRule)[0] || [];
    if (!direction) {
      return;
    }

    const queryField = this.getRulesetQueryField(field, direction);
    this.queryObject[queryField] = ruleValue;
  }

  getSelectedRulesetRule(value, column) {
    const [selectedValue] = value;

    if (typeof selectedValue === "undefined") {
      return null;
    }

    return column.filter_data[selectedValue] || null;
  }

  getRulesetQueryField(field, direction) {
    if (direction === "eq" || direction === "exact") {
      return field;
    }

    return `${field}__${direction}`;
  }

  toString(): string {
    return qs.stringify(this.queryObject);
  }
}

export default class UniversalTableQueryBuilder {
  tableState = {};

  constructor(tableState) {
    this.setTableState(tableState);
  }

  setTableState(tableState) {
    this.tableState = tableState;
  }

  getQueryParams() {
    const { sortBy, pagination, search, filters } = this.tableState;
    const columns = this.tableState.getAllAvailableColumns();
    let query = new UniversalTableQuery();

    return query
      .addLimit(pagination)
      .addOffset(pagination)
      .addOrdering(sortBy)
      .addSearch(search)
      .addFilters(filters, columns)
      .toString();
  }
}
